import type { GettextInterface } from "../../../../../shared/I18n";
import type { DynamicConvoStarterId } from "./selectDynamicConvoStarters";

/**
 * Presentation strings for the Ask Career Scout conversation starters, kept
 * separate from the pure job-data selection logic in
 * {@link ./selectDynamicConvoStarters} so that module stays free of i18n.
 *
 * Each label/message is inlined as a literal `gettext(...)` call because
 * react-gettext-parser only records a msgid for string literals — passing a
 * variable (the old `gt.gettext(chip.label)` shape) extracts nothing. The
 * switch is exhaustive on purpose: a new {@link DynamicConvoStarterId} without
 * a case is a compile error rather than a silently untranslated English string.
 *
 * Analytics logging is intentionally split (View Job starter design, RN-8165):
 * the impression logs the raw English `chip.label` as a locale-independent key,
 * while the tap logs the localized label these resolvers return. This tap↔
 * impression divergence in non-English locales is by design; changing the tap
 * value is a Signals-consumer decision, not an implementation detail.
 */

/**
 * Narrowed view of `GettextInterface` — only `gettext` is exercised here, so
 * callers don't need to thread a full provider and the resolvers stay trivially
 * mockable with an identity `gettext`.
 */
type ConvoStarterI18n = Pick<GettextInterface, "gettext">;

/** Localized text shown on the chip and logged on tap. */
export const getDynamicConvoStarterLabel = (
  gt: ConvoStarterI18n,
  id: DynamicConvoStarterId,
): string => {
  switch (id) {
    case "roleDetails":
      return gt.gettext("What would I be doing?");
    case "qualifications":
      return gt.gettext("What experience do I need?");
    case "hourlyPay":
      return gt.gettext("What is the hourly pay?");
    case "yearlyPay":
      return gt.gettext("What is the yearly pay?");
    case "typicalPay":
      return gt.gettext("What is the typical pay for jobs like this?");
    case "workSchedule":
      return gt.gettext("What is the work schedule?");
    case "location":
      return gt.gettext("Where is this job located?");
    case "remote":
      return gt.gettext("Is this job fully remote?");
    case "fullTime":
      return gt.gettext("Is this a full-time role?");
    case "datePosted":
      return gt.gettext("When was this job posted?");
    case "acceptingApplications":
      return gt.gettext("Is the employer still accepting applications?");
    case "differentQuestion":
      return gt.gettext("I have a different question");
  }
};

/**
 * Localized message sent to Career Scout when a chip is tapped. Only the pinned
 * "different question" chip carries a message distinct from its label; every
 * other chip falls back to its label.
 */
export const getDynamicConvoStarterMessage = (
  gt: ConvoStarterI18n,
  id: DynamicConvoStarterId,
): string => {
  switch (id) {
    case "differentQuestion":
      return gt.gettext("I have a different question about this job");
    default:
      return getDynamicConvoStarterLabel(gt, id);
  }
};
